/**
 * Reports `false` only when clipboard-read is explicitly DENIED.
 *
 * Only Chromium exposes the "clipboard-read" permission. On Firefox/Safari the query
 * throws (or the API is absent) and `onChange` is never called — callers keep their
 * default `readable` value, and the paste itself degrades gracefully if the read is blocked.
 *
 * Returns an unsubscribe function.
 */
export function watchClipboardReadable(
  onChange: (readable: boolean) => void,
  onError?: (reason: unknown) => void,
): () => void {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return () => {};
  }

  let status: PermissionStatus | undefined;
  let unsubscribed = false;

  navigator.permissions
    .query({ name: "clipboard-read" as PermissionName })
    .then((permissionStatus) => {
      if (unsubscribed) return;

      status = permissionStatus;
      const sync = () => onChange(permissionStatus.state !== "denied");

      sync();
      permissionStatus.onchange = sync;
    })
    .catch((reason) => onError?.(reason));

  return () => {
    unsubscribed = true;
    if (status) status.onchange = null;
  };
}
